import { useFormController } from '@dreryk/react-common';
import {
   FormContent,
   FormRouteLeavingPrompt,
   FormValidationSnackbar,
   GridLayout,
   LargeAreaTemplate,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { healthcarePracticeTranslationKeys } from '../../../translations';
import { CityInput } from '../../common/CityInput';
import { EmailAddressInput } from '../../common/EmailAddressInput';
import { FlatInput } from '../../common/FlatInput';
import { HealthcareProviderNameInput } from '../../common/HealthcareProviderNameInput';
import { HouseInput } from '../../common/HouseInput';
import { LogoFileUpload } from '../../common/LogoFileUpload/LogoFileUpload';
import { PhoneNumberInput } from '../../common/PhoneNumberInput';
import { PostcodeInput } from '../../common/PostcodeInput';
import { RegisterBookNumberInput } from '../../common/RegisterBookNumberInput';
import { RegonInput } from '../../common/RegonInput';
import { StreetInput } from '../../common/StreetInput';
import { TaxIdentifierInput } from '../../common/TaxIdentifierInput';
import { HealthcarePracticeFormBean } from '../common/healthcarePractice';
import { SkipAddressInStampCheckbox } from '../fields/SkipAddressInStampCheckbox';

import { HealthcarePracticeFromRpwdlButton } from './HealthcarePracticeFromRpwdlButton';
import { HealthcarePracticeUpdateAction } from './HealthcarePracticeUpdateAction';
import { HealthcarePracticeUpdateFormBean } from './HealthcarePracticeUpdateForm.types';
import { MedicalChamberSelect } from './MedicalChamberSelect';
import { ServiceProvidingLocationsToUpdateTable } from './ServiceProvidingLocationsToUpdateTable';
import { ServiceProvidingLocationUsingHealthcarePracticeDataSynchronizer } from './ServiceProvidingLocationUsingHealthcarePracticeDataSynchronizer';

type HealthcarePracticeUpdateFormProps = {
   readonly healthcarePractice: HealthcarePracticeFormBean;
   readonly onDataChanged: () => Promise<void>;
   readonly verifyAccountConfiguration: () => Promise<void>;
};

const getAreaTemplate = (): LargeAreaTemplate =>
   protectFeature(
      'deprecated',
      [
         'e1 e1 e2 e2 e2 e2 e3 e3 e3 . . . .',
         'e4 e4 e4 e4 e4 e4 . . . . . . .',
         'e5 e5 e5 e5 e6 . . . . . . . .',
         'e7 e7 e7 e7 e8 e9 . . . . . . .',
         'e10 e10 e10 e10 e10 . . . . . . . .',
         'e11 e11 e12 e12 e12 . . . . . . . .',
         'e13 e13 e14 e14 . . . . . . . . .',
         'e15 e15 e15 e15 e15 . . . . . . . .',
      ],
      [
         'e1 e1 e2 e2 e2 e2 . . . . . . .',
         'e3 e3 e3 e3 e3 e3 . . . . . . .',
         'e4 e4 e4 e4 e5 . . . . . . . .',
         'e6 e6 e6 e6 e7 e8 . . . . . . .',
         'e9 e9 e9 e9 e9 . . . . . . . .',
         'e10 e10 e11 e11 e11 . . . . . . . .',
         'e12 e12 e13 e13 . . . . . . . . .',
         'e14 e14 e14 e14 e14 . . . . . . . .',
      ]
   );

// eslint-disable-next-line max-lines-per-function
export const HealthcarePracticeUpdateForm = (props: HealthcarePracticeUpdateFormProps): JSX.Element => {
   const {
      register,
      watch,
      formState,
      trigger,
      handleSubmit,
      reset,
      setValue,
      getValue,
   } = useFormController<HealthcarePracticeUpdateFormBean>({
      ...props.healthcarePractice,
      serviceProvidingLocationsToUpdate: null,
   });

   const healthcarePractice = watch();

   return (
      <>
         <form noValidate>
            <FormContent testId={'practiceData'}>
               <GridLayout area={getAreaTemplate()} testId={'practiceData'}>
                  <RegisterBookNumberInput name={'registerBookNumber'} register={register} required />
                  <MedicalChamberSelect register={register} />
                  {protectFeature(
                     'deprecated',
                     <HealthcarePracticeFromRpwdlButton
                        registerBookNumber={healthcarePractice.registerBookNumber}
                        medicalChamber={healthcarePractice.medicalChamber}
                        getValue={getValue}
                        reset={reset}
                     />
                  )}
                  <HealthcareProviderNameInput name={'name'} register={register} required />
                  <CityInput name={'city'} register={register} required />
                  <PostcodeInput name={'postcode'} register={register} required />
                  <StreetInput name={'street'} register={register} />
                  <HouseInput name={'house'} register={register} required />
                  <FlatInput name={'flat'} register={register} />
                  <SkipAddressInStampCheckbox name={'skipAddressInStamp'} register={register} />
                  <PhoneNumberInput name={'phoneNumber'} register={register} required />
                  <EmailAddressInput name={'emailAddress'} register={register} />
                  <RegonInput name={'regon'} register={register} required type={'regon'} />
                  <TaxIdentifierInput name={'taxIdentifier'} register={register} required />
                  <LogoFileUpload name={'logo'} register={register} />
               </GridLayout>

               {healthcarePractice.serviceProvidingLocationsToUpdate ? (
                  <>
                     <ServiceProvidingLocationsToUpdateTable
                        register={register}
                        healthcarePractice={healthcarePractice}
                        verifyAccountConfiguration={props.verifyAccountConfiguration}
                     />
                     <ServiceProvidingLocationUsingHealthcarePracticeDataSynchronizer
                        watch={watch}
                        setValue={setValue}
                     />
                  </>
               ) : null}
            </FormContent>
         </form>

         <HealthcarePracticeUpdateAction
            showWarningBeforeUpdate={Boolean(healthcarePractice.serviceProvidingLocationsToUpdate)}
            trigger={trigger}
            handleSubmit={handleSubmit}
            onDataChanged={props.onDataChanged}
         />
         <FormRouteLeavingPrompt
            formState={formState}
            title={healthcarePracticeTranslationKeys.leavingPrompt.title}
            message={healthcarePracticeTranslationKeys.leavingPrompt.message}
         />
         <FormValidationSnackbar formState={formState} />
      </>
   );
};
